import MuiButton, {
  type ButtonProps as MuiButtonProps,
} from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import { alpha } from '@mui/material/styles';

type ButtonVariant =
  | 'primary'
  | 'secondary'
  | 'tertiary'
  | 'ghost'
  | 'outlined'
  | 'danger';

type ButtonSize = 'sm' | 'md' | 'lg';

type ButtonProps = Omit<MuiButtonProps, 'variant' | 'size' | 'color'> & {
  variant?: ButtonVariant;
  size?: ButtonSize;
  loading?: boolean;
};

const muiVariant: Record<ButtonVariant, MuiButtonProps['variant']> = {
  primary: 'contained',
  secondary: 'contained',
  tertiary: 'text',
  ghost: 'text',
  outlined: 'outlined',
  danger: 'outlined',
};

const muiColor: Record<ButtonVariant, MuiButtonProps['color']> = {
  primary: 'inherit',
  secondary: 'inherit',
  tertiary: 'inherit',
  ghost: 'inherit',
  outlined: 'inherit',
  danger: 'error',
};

const muiSize: Record<ButtonSize, MuiButtonProps['size']> = {
  sm: 'small',
  md: 'medium',
  lg: 'large',
};

const variantSx: Record<ButtonVariant, MuiButtonProps['sx']> = {
  primary: {
    bgcolor: 'text.primary',
    color: 'background.paper',
    '&:hover': {
      bgcolor: (theme) => alpha(theme.palette.text.primary, 0.86),
      '@media (hover: none)': { bgcolor: 'text.primary' },
    },
  },
  secondary: {
    bgcolor: 'background.paper',
    color: 'text.primary',
    border: 1,
    borderColor: 'divider',
    '&:hover': { bgcolor: 'action.hover', borderColor: 'divider' },
  },
  tertiary: {
    color: 'text.secondary',
    '&:hover': { bgcolor: 'action.hover' },
  },
  ghost: {
    color: 'text.primary',
    '&:hover': { bgcolor: 'action.hover' },
  },
  outlined: {
    color: 'text.primary',
    borderColor: 'divider',
    '&:hover': { bgcolor: 'action.hover', borderColor: 'text.secondary' },
  },
  danger: {},
};

export const Button = ({
  variant = 'primary',
  size = 'md',
  loading = false,
  disabled,
  children,
  sx,
  ...props
}: ButtonProps): JSX.Element => {
  return (
    <MuiButton
      variant={muiVariant[variant]}
      color={muiColor[variant]}
      size={muiSize[size]}
      disableElevation
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      sx={[
        variantSx[variant],
        { position: 'relative' },
        ...(Array.isArray(sx) ? sx : [sx]),
      ]}
      {...props}
    >
      <span
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          visibility: loading ? 'hidden' : 'visible',
        }}
      >
        {children}
      </span>
      {loading ? (
        <CircularProgress
          size={18}
          color="inherit"
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            marginTop: '-9px',
            marginLeft: '-9px',
          }}
        />
      ) : null}
    </MuiButton>
  );
};
